// Little Oak — Home page (Golden Hour).
import { Link } from "react-router-dom";
import { useCatalog, usePublicSettings, useSeason } from "../lib/goldenHourData";
import { Arch, Arrow, Icon, Marquee, NoteCard, Offers, Porthole, ProductCard, Seal, SectionHead, Swash, inr } from "../components/GoldenHour";

const OCCASIONS = [
  { name: "Diwali", sku: "LO-G02", note: "for the visits and the diya corner" },
  { name: "Housewarming", sku: "LO-C17", note: "for the keys to a new door" },
  { name: "Birthday", sku: "LO-G01", note: "for the hard-to-shop-for friend" },
  { name: "Anniversary & Love", sku: "LO-C07", note: "for saying it without saying it" },
  { name: "Wedding favours", sku: "LO-T12", note: "for the couple, and every guest" },
  { name: "Just because", sku: "LO-C03", note: "for no reason at all" },
  { name: "New baby", sku: null, note: "for the newest, smallest person" },
  { name: "Kids' party", sku: null, note: "return gifts that get kept" },
];
const occTo = (name) => `/shop?occasion=${encodeURIComponent(name)}`;

function Hero({ c, s }) {
  const lotus = c.bySku("LO-T01"); const ganesha = c.bySku("LO-I01");
  const season = useSeason();
  return (
    <section className="lo-hero">
      <div className="lo-sun" aria-hidden="true" />
      <div className="lo-wrap lo-hero__grid">
        <div className="lo-hero__copy">
          <span className="lo-eyebrow lo-in lo-in-1">Hand-poured in Pune</span>
          <h1 className="lo-display lo-h1 lo-in lo-in-2">Gifts that make them ask, <em>“where did you get <Swash>this</Swash>?”</em></h1>
          <p className="lo-lede lo-in lo-in-3">Sculptural candles, tealight holders and décor, poured by hand in small batches. Wrapped in kraft, tied with twine, and sent with your note, written by hand.</p>
          <div className="lo-hero__ctas lo-in lo-in-4">
            {season.diwaliOn ? <Link to="/festivals" className="lo-btn lo-btn--primary">Shop Diwali gifts <Arrow /></Link> : <Link to="/shop" className="lo-btn lo-btn--primary">Shop the pieces <Arrow /></Link>}
            <Link to="/gift-finder" className="lo-link">Find their gift in 30 seconds</Link>
          </div>
          <ul className="lo-promises lo-in lo-in-5">
            <li><Icon name="gift" />Gift-wrapped free</li><li><Icon name="pen" />Handwritten note</li><li><Icon name="seal" />Re-pour Promise</li>
          </ul>
        </div>
        <div className="lo-hero__art">
          <div className="lo-hero__frame">
            <div className="lo-hero__niche" aria-hidden="true" />
            {lotus ? <Arch poster={lotus.imageLarge} video={s.film_home_hero_url || lotus.film} focal={lotus.focal} alt={lotus.alt} className="lo-arch-in" eager /> : <div className="lo-arch" />}
            {ganesha ? <div className="lo-hero__port lo-pop"><Porthole poster={ganesha.image} video={ganesha.film} focal={ganesha.focal} alt={ganesha.alt} /></div> : null}
            <Seal className="lo-hero__seal lo-pop" />
          </div>
          {lotus ? (
            <div className="lo-hero__caption lo-in lo-in-5">
              <span className="lo-hand">the Lotus Glow, lit</span>
              <Link to={lotus.to}>{lotus.name} · {inr(lotus.price)} →</Link>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function NightChapter({ c, s }) {
  const season = useSeason();
  if (!season.diwaliOn) return null;
  const windows = ["LO-C01", "LO-G02", "LO-T04"].map(c.bySku).filter(Boolean);
  const rail = ["LO-K01", "LO-C02", "LO-G07", "LO-G01"].map(c.bySku).filter(Boolean);
  const films = s.film_diwali_windows || [];
  return (
    <section className="lo-night" data-night="230">
      <div className="lo-wrap">
        <div className="lo-head lo-head--split">
          <div className="lo-stack" style={{ gap: 18 }}>
            <span className="lo-eyebrow">The Diwali edit</span>
            <h2 className="lo-display lo-h2" data-reveal>Light up <em>the visits.</em></h2>
          </div>
          <div className="lo-stack" style={{ gap: 14 }}>
            <span className="lo-night__greet" lang="hi">शुभ दीपावली</span>
            <p className="lo-lede" data-reveal>For the diya corner, the front door, and the family in another city.{season.retailOn ? <> Order by <b style={{ color: "var(--night-ink)" }}>{season.retailLabel}</b> for Diwali delivery.</> : null}{season.bulkOn ? ` Bulk and corporate orders by ${season.bulkLabel}.` : null}</p>
          </div>
        </div>
        <div className="lo-triptych">
          {windows.map((p, i) => (
            <Link key={p.sku} to={p.to} className="lo-window" data-reveal style={{ "--d": `${i * 0.12}s` }}>
              <Arch poster={p.imageLarge} video={films[i] || p.film} focal={p.focal} alt={p.alt} />
              <div className="lo-window__cap"><h3>{p.short || p.name}</h3><span className="lo-num">{inr(p.price)}</span></div>
            </Link>
          ))}
        </div>
        <div style={{ marginTop: "clamp(48px, 6vw, 80px)" }}><Offers offers={s.offers || ["Buy 2 holders, get the 3rd free", "Free Little Buddha on orders over ₹999", "Free shipping over ₹699"]} /></div>
        <div className="lo-grid" style={{ marginTop: "clamp(48px, 6vw, 80px)" }}>{rail.map((p) => <ProductCard key={p.sku} item={p} />)}</div>
        <div className="lo-row" style={{ marginTop: 48, justifyContent: "center" }}>
          <Link to="/festivals" className="lo-btn lo-btn--light">Shop the Diwali edit <Arrow /></Link>
          <Link to="/bulk" className="lo-link" style={{ color: "var(--night-ink)" }}>Gifting 25 or more?</Link>
        </div>
      </div>
    </section>
  );
}

function Occasions({ c }) {
  return (
    <section className="lo-section">
      <div className="lo-wrap">
        <SectionHead split eyebrow="Shop by occasion" title={<>Who's it <em>for?</em></>} lede="Pick the moment and we'll show you what people give for it."
          action={<Link to="/gift-finder" className="lo-link">Not sure? Take the 30-second Gift Finder <Arrow /></Link>} />
        <div className="lo-occasions">
          {OCCASIONS.map((o, i) => {
            const p = (o.sku && c.bySku(o.sku)) || (c.items || []).find((x) => x.image && (x.occasions || []).includes(o.name));
            return (
              <Link key={o.name} to={occTo(o.name)} className={`lo-occ ${p ? "" : "lo-occ--text"}`} data-reveal style={{ "--d": `${(i % 4) * 0.07}s` }}>
                {p ? <Arch poster={p.image} focal={p.focal} alt="" film={false} /> : (
                  <div className="lo-arch lo-arch--frame" style={{ background: i % 2 ? "#DCE2D3" : "var(--blush)" }}><span className="lo-hand">{o.note}</span></div>
                )}
                <div><h3>{o.name}</h3>{p ? <span className="lo-hand">{o.note}</span> : <span className="lo-tiny">Pieces coming soon</span>}</div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Pieces({ c }) {
  const list = ["LO-C03", "LO-T01", "LO-C07", "LO-I01", "LO-C01", "LO-P11", "LO-K01", "LO-T12", "LO-C17"].map(c.bySku).filter(Boolean);
  return (
    <section className="lo-section" style={{ paddingTop: 0 }}>
      <div className="lo-wrap">
        <SectionHead split eyebrow="The pieces" title={<>Start with <em>these.</em></>} lede="Nine favourites from the studio, each one hand-poured in Pune and finished by hand."
          action={<Link to="/shop" className="lo-link">Shop all pieces <Arrow /></Link>} />
        <div className="lo-grid lo-grid--editorial">{list.map((p, i) => <ProductCard key={p.sku} item={p} feature={i === 0} style={{ "--d": `${(i % 4) * 0.06}s` }} />)}</div>
      </div>
    </section>
  );
}

export function Studio({ c, s, link = true }) {
  const p = c.bySku("LO-C02");
  return (
    <section className="lo-section lo-section--linen">
      <div className="lo-wrap lo-studio">
        <div className="lo-studio__art" data-reveal>
          {p ? <Arch poster={p.imageLarge} video={s.film_studio_url} focal={p.focal} alt="Praying Buddha candle in sage, finished by hand" className="lo-arch--tall" /> : null}
          <span className="lo-hand">made in Pune, wrapped by hand</span>
        </div>
        <div className="lo-stack" style={{ gap: 26 }}>
          <span className="lo-eyebrow">The studio</span>
          <h2 className="lo-display lo-h2" data-reveal>Poured by hand, <em>a few at a time.</em></h2>
          <p className="lo-lede" data-reveal>Every Little Oak piece starts as a pour into a silicone mould in our Pune studio. Once it sets, we unmould it, finish it by hand, and wrap it ourselves.</p>
          <ol className="lo-steps">
            <li className="lo-step" data-reveal><span className="lo-step__n">01</span><h3>Poured</h3><p>Into moulds we own, a small batch at a time. Some designs have a single mould, so those batches are tiny.</p></li>
            <li className="lo-step" data-reveal style={{ "--d": ".08s" }}><span className="lo-step__n">02</span><h3>Unmoulded and finished</h3><p>Each piece is checked, trimmed and coloured by hand before it's packed.</p></li>
            <li className="lo-step" data-reveal style={{ "--d": ".16s" }}><span className="lo-step__n">03</span><h3>Wrapped with your note</h3><p>Kraft paper, cotton twine, and your message written out by hand.</p></li>
          </ol>
          {link ? <Link to="/our-studio" className="lo-link">Read about the studio <Arrow /></Link> : null}
        </div>
      </div>
    </section>
  );
}

export function Hamper({ c, onAdd }) {
  const p = c.bySku("LO-G07");
  if (!p) return null;
  return (
    <div className="lo-hamper">
      <Link to={p.to} className="lo-hamper__img" data-reveal><img src={p.imageLarge} alt={p.alt} loading="lazy" /></Link>
      <div className="lo-hamper__copy">
        <span className="lo-eyebrow">The big one</span>
        <h3 className="lo-display lo-h3" data-reveal>{p.name}</h3>
        <p className="lo-buy__line">{p.card_line}</p>
        <ul className="lo-contents">{(p.contents || []).map((x) => <li key={x}>{x}</li>)}</ul>
        <span className="lo-price lo-price--big">{inr(p.price)}</span>
        <div className="lo-row">
          <button className="lo-btn lo-btn--primary" onClick={() => onAdd(p)}>Add to basket</button>
          <Link to={p.to} className="lo-link">See what's inside</Link>
        </div>
      </div>
    </div>
  );
}

function Boxes({ c, onAdd }) {
  const row = ["LO-G02", "LO-G03", "LO-G01"].map(c.bySku).filter(Boolean);
  return (
    <section className="lo-section">
      <div className="lo-wrap">
        <SectionHead split eyebrow="Gift boxes" title={<>Ready to give, <em>already wrapped.</em></>} lede="Hand-poured pieces packed together in kraft, with twine and your note. You pick the colour of every piece."
          action={<Link to="/gift-boxes" className="lo-link">All gift boxes <Arrow /></Link>} />
        <Hamper c={c} onAdd={onAdd} />
        <div className="lo-boxrow">{row.map((p) => <ProductCard key={p.sku} item={p} />)}</div>
      </div>
    </section>
  );
}

export function NoteBand({ c, s }) {
  const p = c.bySku("LO-G01");
  return (
    <section className="lo-section lo-noteband">
      <div className="lo-wrap lo-noteband__grid">
        {p ? <Arch poster={p.imageLarge} video={s.film_giftwrap_url} focal={p.focal} alt="A gift box tied with twine and a handwritten note" /> : <div />}
        <div className="lo-notecard-wrap">
          <span className="lo-eyebrow">On every order</span>
          <h2 className="lo-display lo-h2" data-reveal>Write the note. <em>We'll write it by hand.</em></h2>
          <p className="lo-lede">Type it at checkout. We copy it onto a card in ink and tie it to the box. Free on every order, up to 150 characters.</p>
          <NoteCard id="home-note" />
        </div>
      </div>
    </section>
  );
}

export function BulkStrip({ c }) {
  const season = useSeason();
  const a = c.bySku("LO-T12"); const b = c.bySku("LO-K01");
  return (
    <section className="lo-section">
      <div className="lo-wrap lo-bulk">
        <div className="lo-bulk__pics" data-reveal>
          <div>{a ? <img src={a.image} alt={a.alt} loading="lazy" style={{ objectPosition: a.focal }} /> : null}</div>
          <div>{b ? <img src={b.image} alt={b.alt} loading="lazy" style={{ objectPosition: b.focal }} /> : null}</div>
        </div>
        <div className="lo-stack" style={{ gap: 22 }}>
          <span className="lo-eyebrow">Bulk & custom</span>
          <h2 className="lo-display lo-h2" data-reveal>Gifting 25 <em>or more?</em></h2>
          <p className="lo-lede">For weddings, office Diwali gifts and return gifts.{season.bulkOn ? ` Corporate Diwali orders close on ${season.bulkLabel}.` : ""}</p>
          <ul className="lo-ticks">
            <li><Icon name="check" />Custom colours to match the occasion</li><li><Icon name="check" />Name tags and your message on every piece</li>
            <li><Icon name="check" />GST invoice for companies</li><li><Icon name="check" />Delivered across India, or to one address</li>
          </ul>
          <div className="lo-row"><Link to="/bulk" className="lo-btn lo-btn--ghost">Get a quote <Arrow /></Link><span className="lo-small">We reply on WhatsApp with a quote.</span></div>
        </div>
      </div>
    </section>
  );
}

export function PaintArt() {
  return (
    <div className="lo-paint" aria-hidden="true">
      <svg viewBox="0 0 400 320">
        <ellipse cx="200" cy="290" rx="170" ry="16" fill="rgba(60,70,50,.12)" />
        <rect x="70" y="120" width="260" height="160" rx="8" fill="#C9A57A" />
        <path d="M70 120l40-48h180l40 48z" fill="#D9B98E" />
        <rect x="70" y="120" width="260" height="20" fill="rgba(0,0,0,.06)" />
        <text x="200" y="215" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontSize="30" fill="#4A3420" fontStyle="italic">Little Oak</text>
        <text x="200" y="244" textAnchor="middle" fontFamily="Manrope, sans-serif" fontWeight="700" fontSize="10.5" letterSpacing="3" fill="#6B4A2B">COLOUR-IT-YOURSELF</text>
        {[["#E1A22A", 105, 96], ["#C65A3A", 150, 84], ["#23409A", 250, 84], ["#1F6B4F", 296, 96], ["#7B2D6E", 200, 78]].map(([col, x, y], i) => (
          <g key={i}><ellipse cx={x} cy={y + 18} rx="17" ry="6" fill="rgba(0,0,0,.12)" /><rect x={x - 15} y={y - 6} width="30" height="24" rx="6" fill="#F4F0E8" /><ellipse cx={x} cy={y - 6} rx="15" ry="5.5" fill={col} /></g>
        ))}
        <g transform="rotate(-28 300 60)"><rect x="250" y="56" width="110" height="7" rx="3.5" fill="#B08A5A" /><path d="M360 56c10 0 18 3.5 18 3.5s-8 3.5-18 3.5z" fill="#3A3835" /><path d="M378 59.5c8-1 12 0 14 1-2 1-6 2-14 1z" fill="#E1A22A" /></g>
      </svg>
    </div>
  );
}

function KitsTeaser({ onKitsLead }) {
  return (
    <section className="lo-section lo-section--tight">
      <div className="lo-wrap">
        <div className="lo-kits-card" data-reveal>
          <div className="lo-stack" style={{ gap: 18 }}>
            <span className="lo-eyebrow">Coming soon</span>
            <h2 className="lo-display lo-h3">Colour-it-yourself kits</h2>
            <p className="lo-body" style={{ color: "var(--ink-2)" }}>Little figurines to paint, with paints, a brush and a picture guide in the box. Made for rainy afternoons and birthday return gifts.</p>
            <form className="lo-inline-form" onSubmit={(e) => { e.preventDefault(); onKitsLead(new FormData(e.currentTarget).get("kwa")); e.currentTarget.reset(); }}>
              <label className="lo-sr" htmlFor="kits-wa">WhatsApp number</label>
              <input id="kits-wa" name="kwa" className="lo-input" type="tel" inputMode="numeric" placeholder="WhatsApp number" required />
              <button className="lo-btn lo-btn--primary" type="submit">Tell me first</button>
            </form>
            <Link to="/kits" className="lo-link">See the kits <Arrow /></Link>
          </div>
          <PaintArt />
        </div>
      </div>
    </section>
  );
}

export default function HomeGoldenHour({ onAdd, onKitsLead }) {
  const c = useCatalog();
  const s = usePublicSettings();
  return (
    <div className="lo-page">
      <Hero c={c} s={s} />
      <Marquee items={["Poured by hand in Pune", "Gift-wrapped with a handwritten note", "Re-poured free if it arrives broken", "COD and UPI", "Delivered across India"]} />
      <NightChapter c={c} s={s} />
      <Occasions c={c} />
      <Pieces c={c} />
      <Studio c={c} s={s} />
      <Boxes c={c} onAdd={onAdd} />
      <NoteBand c={c} s={s} />
      <BulkStrip c={c} />
      <KitsTeaser onKitsLead={onKitsLead} />
    </div>
  );
}
